/**
 * Session upkeep before and after each request — legacy's token interceptor
 * and 401 handler (apiservice.js:90-250, :276-296), in one place.
 *
 * Before a request (`ensureFreshSession`), with a token held:
 *  1. the jwt_token cookie is gone — another tab or *.amd.com app signed out —
 *     so the session is over (apiservice.js:173-205);
 *  2. the token has passed its `exp` — get a new one from Common Services'
 *     /getRefreshToken with the Okta refresh token, exactly as legacy does
 *     (:111-165), and carry on with the new token;
 *  3. that refresh fails — the session is over (:214-243).
 * After a request (`expireSession` from interceptors.js): a 401 ends the
 * session, showing the server's message (:277-296).
 *
 * "Over" means legacy's ending: the session cookies are cleared, "Session
 * expired. Please login again." is shown, and the browser goes to the Okta
 * logout link (VITE_OKTA_LOGOUT_LINK) — on the Advisory sign-in only. With the
 * local form (localhost) there is no Okta session to end, so the sign-in
 * screen comes up in place, with the message as a toast.
 *
 * Fixed from legacy:
 *  - one refresh for a burst of requests (legacy refreshes once PER request
 *    that finds the token expired — the 15s notifications poll included);
 *  - a malformed token reads as expired instead of throwing out of the
 *    interceptor (jwt.js);
 *  - no refresh_token cookie, or no Common Services address (VITE_CS_API),
 *    ends the session without sending a request that cannot succeed (legacy
 *    posts `refresh_token: undefined`);
 *  - no Okta client id / secret is sent. Legacy put them in the bundle as Basic
 *    auth; Common Services' backend holds them itself now, so the request
 *    carries only the refresh token;
 *  - the domain cookie is cleared too (sessionMirror's one list).
 */
import axios from 'axios';

import { buildCsLogoutUrl } from '@config/csLogout';
import { env } from '@config/env';
import { getLoginFlags } from '@config/loginMode';
import { signedOut } from '@security/auth/authSlice';
import { isTokenExpired, readTokenClaims } from '@security/auth/jwt';
import { patchSession, readRefreshToken, readSessionToken } from '@security/auth/sessionMirror';
import { clearAccessToken, getAccessToken, setAccessToken } from '@security/auth/tokenStore';
import { redirectTo } from '@utils/navigation';
import { getVariant } from '@variant/appMode';

import { API_ENDPOINTS } from './apiEndpoints';

/** Legacy wording, verbatim (apiservice.js:201). */
export const SESSION_EXPIRED_MESSAGE = 'Session expired. Please login again.';

const { LOGIN, GET_USER_TOKEN, REFRESH_TOKEN } = API_ENDPOINTS.AUTH;

/**
 * The sign-in calls run before there is a session, so none of this applies to
 * them (legacy skips '/login' and '/getRefreshToken', :170). getUserToken's URL
 * is absolute (the Common Services host), hence endsWith.
 */
export function isSessionFreeUrl(url = '') {
  return url === LOGIN || url.endsWith(GET_USER_TOKEN) || url.endsWith(REFRESH_TOKEN);
}

/**
 * End the session: clear it, leave the message for SessionNotice, and on the
 * Advisory sign-in go to the Okta logout link. Runs once — later callers in the
 * same burst find no token and stop.
 */
export function expireSession(dispatch, message = SESSION_EXPIRED_MESSAGE) {
  if (!getAccessToken()) return;
  clearAccessToken(); // at once, so requests already queued see no session
  dispatch(signedOut({ notice: message || SESSION_EXPIRED_MESSAGE }));

  // Common Services' ?state=logout for this product, which sends the user back
  // here once its side is cleared (config/csLogout.js).
  const logoutUrl = getLoginFlags().advisoryLogin
    ? buildCsLogoutUrl(env.csLogoutUrl, getVariant().shortName)
    : null;
  if (logoutUrl) redirectTo(logoutUrl);
}

/**
 * Take over a newer, unexpired token another tab (or *.amd.com app) has
 * already written to the shared cookie. Saves a second refresh — and with
 * rotating refresh tokens, a second refresh using the same, now spent,
 * refresh token would fail and sign every tab out.
 *
 * @returns {boolean} true when one was adopted
 */
function adoptSharedToken() {
  const shared = readSessionToken();
  if (!shared || shared === getAccessToken() || isTokenExpired(shared)) return false;
  const { expiresInSeconds } = readTokenClaims(shared) ?? { expiresInSeconds: 0 };
  setAccessToken(shared, expiresInSeconds);
  return true;
}

/**
 * Common Services' token refresh: the Okta refresh token in the body, and NO
 * client credentials — the backend keeps the Okta client id and secret itself
 * (legacy sent them as Basic auth, which put the secret in the public bundle).
 * On success the new tokens go to memory and the cookies.
 *
 * @returns {Promise<void>} rejects when no new token came back
 */
async function requestNewToken() {
  const refreshToken = readRefreshToken();
  if (!refreshToken) throw new Error('No refresh token cookie.');
  if (!env.csApiBaseUrl) {
    // eslint-disable-next-line no-console
    console.error('[session] Token refresh is not configured: set VITE_CS_API.');
    throw new Error('Token refresh is not configured.');
  }

  // Plain axios, not apiClient: this call must not carry the expired bearer
  // token (the request interceptor would try to refresh it again).
  // Deliberately no Authorization header.

  const startedWith = getAccessToken();
  const response = await axios.post(
    `${env.csApiBaseUrl}${REFRESH_TOKEN}`,
    { grant_type: 'refresh_token', refresh_token: refreshToken },
    {
      timeout: env.apiTimeoutMs,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  );

  const { access_token: accessToken, refresh_token: nextRefreshToken } = response?.data ?? {};
  if (!accessToken) throw new Error('The refresh returned no token.');
  // Signed out (or in again) while the call was out: writing now would bring
  // back a session the user just ended.
  if (getAccessToken() !== startedWith) throw new Error('The session changed during the refresh.');

  patchSession({ refreshToken: nextRefreshToken, accessToken });
  const { expiresInSeconds } = readTokenClaims(accessToken) ?? { expiresInSeconds: 0 };
  setAccessToken(accessToken, expiresInSeconds);
}

let refreshing = null;

/** One refresh at a time; every request that finds the token expired waits on it. */
function refreshOnce() {
  if (!refreshing) {
    refreshing = requestNewToken().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

/**
 * Make sure the session is usable before a request goes out.
 *
 * @returns {Promise<boolean>} false when the session has ended — send nothing
 */
export async function ensureFreshSession(dispatch) {
  if (!getAccessToken()) return true; // signed out: nothing to keep fresh

  // A browser with cookies off never had the cookie, so its absence says
  // nothing there; the in-memory session carries on.
  const cookiesOn = typeof navigator === 'undefined' || navigator.cookieEnabled !== false;
  if (cookiesOn && !readSessionToken()) {
    expireSession(dispatch);
    return false;
  }

  if (!isTokenExpired(getAccessToken())) return true;
  if (adoptSharedToken()) return true;

  try {
    await refreshOnce();
    return true;
  } catch (error) {
    // Another tab may have won the refresh while ours failed.
    if (adoptSharedToken()) return true;
    // eslint-disable-next-line no-console
    console.error('[session] Token refresh failed:', error);
    expireSession(dispatch);
    return false;
  }
}
